/**
 * Dashboard onboarding gate — the SINGLE source of truth for which dashboard
 * module cards are locked during first-run setup.
 *
 * RBAC is not finalized. Everything role/sequence related is decided here so a
 * future, stricter policy is a one-file change: the dashboard route only renders
 * what this module returns.
 *
 * To extend later:
 *   - Tighten WHO may edit the customer account → change the single call site
 *     that sets `canEditCustomer` (today: `useIsAdmin()` = owner + admin).
 *   - Add steps / make the sequence stricter → edit `cardGate` (and its tests).
 *   - Gate a NEW card → add an href branch; anything unlisted falls into the
 *     default "needs a submitted product" bucket.
 */

/** Setup milestones + this user's customer-edit permission. Plain booleans so
 *  the logic stays pure and unit-testable; the route derives them from queries. */
export interface OnboardingState {
  /** false until the user has accepted the current Terms; gates everything.
   *  Biased to true while loading so an accepted user never flashes the terms screen. */
  termsAccepted: boolean;
  /** Customer account has been submitted (status === 'submitted'). */
  customerSubmitted: boolean;
  /** At least one submitted product exists for the customer. */
  hasProduct: boolean;
  /** This user may edit the customer account. Today = owner/admin (isAdmin). */
  canEditCustomer: boolean;
  /** Admin Settings "Simulators" switch (from /api/auth/me). Optional and
   *  biased to ENABLED when absent/loading, so existing callers and a slow
   *  fetch never flash a greyed tab. Applies to everyone, members included. */
  simulatorsEnabled?: boolean;
}

export interface CardGate {
  /** Disable the card's primary action. */
  locked: boolean;
  /** One-line reason shown on the card when locked. */
  note?: string;
}

const DASHBOARD_HREF = '/dashboard';
const CUSTOMER_HREF = '/customer-account';
const PRODUCT_HREF = '/product-account';
const SIMULATORS_HREF = '/simulators';

/** The admin switch: only an explicit false disables (absent = enabled). */
function simulatorsDisabled(state: OnboardingState): boolean {
  return state.simulatorsEnabled === false;
}

const UNLOCKED: CardGate = { locked: false };

/** Top-nav tabs that stay reachable throughout onboarding — a user can always
 *  open the Dashboard and view their Company Account. */
const NAV_ALWAYS_AVAILABLE = new Set<string>([DASHBOARD_HREF, CUSTOMER_HREF]);

/**
 * The terms gate precedes everything: until accepted, no onboarding step is
 * reachable. Every user (founder + invited member) accepts their own terms.
 */
export function termsGateActive(state: OnboardingState): boolean {
  return !state.termsAccepted;
}

/**
 * Decide whether a single dashboard card is locked, given onboarding state.
 *
 * `href` keys the card (matches the card's destination URL). Cards without an
 * href (e.g. "coming soon") fall into the default branch; the route keeps them
 * disabled on their own and suppresses the note.
 */
export function cardGate(href: string | undefined, state: OnboardingState): CardGate {
  // The terms gate precedes the whole sequence — until accepted, EVERY card
  // (including Dashboard + Company Account) is locked.
  if (termsGateActive(state)) {
    return { locked: true, note: 'Accept the Terms & Conditions to continue' };
  }

  if (href === CUSTOMER_HREF) {
    return state.canEditCustomer
      ? UNLOCKED
      : { locked: true, note: 'Managed by your account owner' };
  }

  // The admin Simulators switch applies to EVERYONE (members included), so it
  // sits before the members' early-unlock below. Deliberately NO note: the
  // nav renders a note-less lock as a quiet grey row — no lock icon, no
  // explanation — per the product call that a switched-off tool shouldn't
  // advertise why.
  if (href === SIMULATORS_HREF && simulatorsDisabled(state)) {
    return { locked: true };
  }

  // The first-run sequence only applies to whoever can edit the customer
  // account (the owner/admin doing setup). Invited members joined an
  // already-running company — their working functions are always available.
  if (!state.canEditCustomer) return UNLOCKED;

  if (href === PRODUCT_HREF) {
    return state.customerSubmitted
      ? UNLOCKED
      : { locked: true, note: 'Available after you submit your Company Account' };
  }

  // Everything else (agents, simulators) operates on a product.
  return state.hasProduct
    ? UNLOCKED
    : { locked: true, note: 'Available once you have a submitted product' };
}

/**
 * Whether a top-nav tab is locked during first-run onboarding.
 *
 * Mirrors `cardGate`'s sequence (Product after the Company Account, agents
 * after a product) so the navbar and the dashboard cards stay in agreement —
 * but always leaves the Dashboard and Company Account tabs reachable. Members
 * (who joined an already-running company) are never gated.
 */
export function navTabLocked(href: string, state: OnboardingState): boolean {
  // Terms gate locks the WHOLE sidebar (Dashboard + Company Account included)
  // until the user accepts — matching the route guard that redirects typed URLs.
  if (termsGateActive(state)) return true;
  if (NAV_ALWAYS_AVAILABLE.has(href)) return false;
  // Admin switch — before the members' early-unlock: it applies to everyone.
  if (href === SIMULATORS_HREF && simulatorsDisabled(state)) return true;
  if (!state.canEditCustomer) return false;
  if (href === PRODUCT_HREF) return !state.customerSubmitted;
  return !state.hasProduct;
}

/**
 * Route-level lock for TYPED URLs (OnboardingRouteGuard) — same rule as
 * `navTabLocked` EXCEPT the admin Simulators switch: the tab greys out, but
 * existing result pages (e.g. /simulators/dossier?run_id=N) stay reachable by
 * URL — the backend keeps completed runs readable and refuses new runs/PDFs
 * on its own, so redirecting here would hide a customer's finished report.
 * The onboarding sequence still gates the section as before.
 */
export function routeGuardLocked(href: string, state: OnboardingState): boolean {
  if (href === SIMULATORS_HREF && simulatorsDisabled(state)) {
    return navTabLocked(href, { ...state, simulatorsEnabled: true });
  }
  return navTabLocked(href, state);
}

/**
 * Short contextual instruction for the dashboard, or `null` when there's
 * nothing to guide (company already set up, or the user isn't onboarding).
 */
export function onboardingMessage(state: OnboardingState): string | null {
  if (!state.canEditCustomer) return null;
  if (!state.customerSubmitted) {
    return 'Welcome. Start by setting up your Company Account — the other functions unlock as you go.';
  }
  if (!state.hasProduct) {
    return 'Company Account submitted. Next, create your first Product Account.';
  }
  return null;
}

// ── Getting-started checklist ───────────────────────────────────────────

export interface GettingStartedStep {
  key: 'company' | 'product' | 'agent' | 'team';
  label: string;
  /** One-line guidance under the label. */
  detail: string;
  done: boolean;
  /** Where the row links — omitted on purpose for the agent step (the
   *  platform doesn't prescribe which agent to run first). */
  href?: string;
  /** Actionable now — the previous milestone is met (mirrors the nav gate). */
  available: boolean;
  /** Nice-to-do, not part of the unlock sequence — rendered with an
   *  "Optional" tag and never blocks the card from completing. */
  optional?: boolean;
}

/**
 * The dashboard's first-run checklist — the same three milestones the
 * lock sequence enforces, shown as visible progress instead of a tour
 * overlay. State-derived (re-derived from live queries every render), so
 * it can't drift from reality, can't be dismissed by accident, and needs
 * no "seen" persistence.
 *
 * Returns `null` when there's nothing to show: members who joined an
 * already-running company, and accounts that finished the three CORE
 * steps (the optional team row never holds the card hostage).
 */
export function gettingStartedSteps(
  state: OnboardingState,
  ranFirstAgent: boolean,
  teamInvited = false,
): GettingStartedStep[] | null {
  if (!state.canEditCustomer) return null;
  if (state.customerSubmitted && state.hasProduct && ranFirstAgent) return null;
  return [
    {
      key: 'company',
      label: 'Set up your Company Account',
      detail: 'Your legal entity, contacts, and target markets.',
      done: state.customerSubmitted,
      href: CUSTOMER_HREF,
      available: true,
    },
    {
      key: 'product',
      label: 'Register your first product',
      detail: 'A device, drug, or biologic — wizard or file upload.',
      done: state.hasProduct,
      href: PRODUCT_HREF,
      available: state.customerSubmitted,
    },
    {
      key: 'agent',
      label: 'Run your first AI agent',
      detail:
        'Open any agent from the sidebar — start with Regulatory ' +
        'Strategy to set your pathway before finding predicates.',
      done: ranFirstAgent,
      available: state.hasProduct,
    },
    {
      key: 'team',
      label: 'Invite your team',
      detail: 'Add admins, editors, or viewers from Team & invites.',
      done: teamInvited,
      href: '/team',
      available: true,
      optional: true,
    },
  ];
}
